import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserCheck, Plus, Pencil, Trash2, Mail, Phone, Briefcase } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

const emptyForm = { nombre: "", email: "", telefono: "", especialidad: "" };

function Abogados({ user, onLogout }) {
  const [abogados, setAbogados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get("/abogados");
      setAbogados(res.data);
    } catch { toast.error("Error cargando abogados"); }
    finally { setLoading(false); }
  };

  const openNew = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (ab) => { setForm({ nombre: ab.nombre, email: ab.email, telefono: ab.telefono || "", especialidad: ab.especialidad || "" }); setEditingId(ab.id); setShowForm(true); };

  const handleSave = async () => {
    if (!form.nombre || !form.email) { toast.error("Nombre y email son obligatorios"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/abogados/${editingId}`, form);
        toast.success("Abogado actualizado");
      } else {
        await api.post("/abogados", form);
        toast.success("Abogado creado");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este abogado del directorio?")) return;
    try {
      await api.delete(`/abogados/${id}`);
      toast.success("Abogado eliminado");
      load();
    } catch { toast.error("Error eliminando"); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "var(--primary)" }} />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="abogados-page">
      <TopNav user={user} onLogout={onLogout} />

      <main className="container mx-auto px-6 py-6 max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--accent-blue-light)" }}>
              <UserCheck className="h-5 w-5" style={{ color: "var(--accent-blue)" }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: "var(--text)" }}>Directorio de Abogados</h1>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>{abogados.length} abogado{abogados.length !== 1 ? "s" : ""} registrado{abogados.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <Button onClick={openNew} className="ls-btn-primary h-8 text-xs" data-testid="new-abogado-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo Abogado
          </Button>
        </div>

        {abogados.length === 0 ? (
          <div className="ls-card p-12 text-center" style={{ borderStyle: "dashed" }}>
            <UserCheck className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--text-light)" }} />
            <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>No hay abogados registrados</p>
            <Button onClick={openNew} size="sm" className="ls-btn-primary text-xs">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar primer abogado
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {abogados.map(ab => (
              <div key={ab.id} className="ls-card p-4" data-testid={`abogado-card-${ab.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: "var(--accent-blue)" }}>
                      {ab.nombre[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: "var(--text)" }}>{ab.nombre}</p>
                      {ab.especialidad && <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{ab.especialidad}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(ab)} className="p-1.5 rounded-md hover:bg-slate-100 transition-colors" data-testid={`edit-abogado-${ab.id}`}>
                      <Pencil className="h-3.5 w-3.5" style={{ color: "var(--text-muted)" }} />
                    </button>
                    <button onClick={() => handleDelete(ab.id)} className="p-1.5 rounded-md hover:bg-red-50 transition-colors" data-testid={`delete-abogado-${ab.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  <div className="flex items-center gap-2"><Mail className="h-3 w-3" style={{ color: "var(--text-muted)" }} />{ab.email}</div>
                  {ab.telefono && <div className="flex items-center gap-2"><Phone className="h-3 w-3" style={{ color: "var(--text-muted)" }} />{ab.telefono}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Abogado" : "Nuevo Abogado"}</DialogTitle>
            <DialogDescription>Datos del abogado para el directorio</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium">Nombre completo *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre del abogado" data-testid="abogado-nombre-input" />
            </div>
            <div>
              <Label className="text-xs font-medium">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="correo@ejemplo.cl" data-testid="abogado-email-input" />
            </div>
            <div>
              <Label className="text-xs font-medium">Teléfono</Label>
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+56 9 1234 5678" data-testid="abogado-telefono-input" />
            </div>
            <div>
              <Label className="text-xs font-medium">Especialidad</Label>
              <Input value={form.especialidad} onChange={(e) => setForm({ ...form, especialidad: e.target.value })} placeholder="Ej: Derecho Civil, Laboral" data-testid="abogado-especialidad-input" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full ls-btn-primary" data-testid="save-abogado-btn">
              {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear Abogado"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Abogados;
